import React from 'react';
import {withRouter} from 'react-router-dom';
import styled from 'styled-components';

import Icon from '../Icon';

function Button({primary = false, secondary = false, tertiary = false, blank = false, shadow, to, onClick, history, children, ...props}) {
  // Determine which style of button to display.
  let ButtonType;

  switch (true) {
    case secondary:
      ButtonType = SecondaryButton;
      break;

    case tertiary:
      shadow = false;
      ButtonType = TertiaryButton;
      break;

    case blank:
      ButtonType = BlankButton;
      break;

    default:
      primary = true;
      ButtonType = PrimaryButton;
  }

  if (to) {
    // If the button is acting as a link, override the onClick function.
    onClick = () => history.push(to);
  }

  if (!blank) {
    // Modify icons to fit in buttons properly.
    children = React.Children.map(children, child => {
      if (child && typeof child === 'object' && child.type === Icon) {
        return React.cloneElement(child, {
          size: props.large ? '1.25em' : '1em',
          style: {
            marginBottom: '-0.125rem'
          }
        });
      }

      return child;
    });
  }

  if (primary && shadow !== false) {
    // Unless the shadow is explicitly disabled, primary buttons will have a shadow.
    shadow = true;
  }

  return (
    <ButtonType type="button" shadow={shadow} center={true} {...props} onClick={onClick}>
      {children}
    </ButtonType>
  );
}

const BlankButton = styled.button`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: ${props => props.readOnly ? 'default' : 'pointer'};
  outline: inherit;
  text-align: left;

  :disabled {
    opacity: 0.6;
    cursor: default;
  }

  ${props => {
    if (props.block) {
      return `
        display: block;
        width: 100%;
      `;
    }
  }}
`;

const BaseButton = styled.button`
  display: inline-block;
  border: 0;
  font-weight: 400;
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  padding: 0.4375rem 0.8125rem;
  font-size: 1rem;
  line-height: 1.5;
  border-radius: .25rem;
  cursor: ${props => props.readOnly ? 'default' : 'pointer'};
  text-align: ${props => props.center ? 'center' : 'start'};
  width: ${props => props.width || 'auto'};

  :disabled {
    opacity: 0.6;
    cursor: default;
  }

  ${props => {
    if (props.large) {
      return `
        padding: 0.5rem 1rem;
        font-size: 1.25rem;
        line-height: 1.5;
        border-radius: 0.3rem;
      `;
    }
  }}

  ${props => {
    if (props.block) {
      return `
        display: block;
        width: 100%;
      `;
    }
  }}

  ${props => {
    if (props.shadow) {
      return `
        box-shadow: 0 0.125rem 0 rgba(90, 97, 105, .11),
                    0 0.25rem 0.5rem rgba(90, 97, 105, .12),
                    0 0.625rem 0.625rem rgba(90, 7, 105, .06), 
                    0 0.4375rem 4.375rem rgba(90, 97, 105, .1);
      `;
    }
  }}
    
  ${props => {
    if (props.grouped) {
      const radius = props.large ? '0.3rem' : '0.25rem';

      if (props.groupFirst) {
        return `
          border-radius: ${radius} 0 0 ${radius};
          margin-right: -0.0625rem;
        `;
      }

      if (props.groupLast) {
        return `
          border-radius: 0 ${radius} ${radius} 0;
        `;
      }

      return `
        border-radius: 0;
        margin-right: -0.0625rem;
      `;
    }
  }}
`;

const PrimaryButton = styled(BaseButton)`
  background: ${props => props.destructive ? 'linear-gradient(40deg, #B4131A, #D1161E)' : 'linear-gradient(40deg, #0049ee, #107aff)'};
  color: #fff;

  ${props => {
    if (!props.readOnly && !props.disabled) {
      if (props.destructive) {
        return `
          :hover {
            background: #D1161E;
          }
        
          :active {
            background: #B4131A;
          }
        `;
      }

      return `
        :hover {
          background: #107aff;
        }
      
        :active {
          background: #0049ee;
        }
      `;
    }
  }}
`;

const SecondaryButton = styled(BaseButton)`
  background: ${props => props.active ? 'rgba(0, 73, 238, 0.1)' : 'transparent'};
  color: #0049ee;
  padding: 0.375rem 0.75rem;
  border: 0.0625rem solid #107aff;

  ${props => {
    if (!props.readOnly && !props.disabled) {
      return `
        :hover {
          background: rgba(0, 73, 238, 0.05);
        }
      
        :active {
          background: rgba(0, 73, 238, 0.1);
        }
      `;
    }
  }}
`;

const TertiaryButton = styled(SecondaryButton)`
  border: 0.0625rem solid transparent;
`;

function ButtonGroup({children, ...props}) {
  const nonNullChildren = React.Children.map(children, child => child).filter(Boolean);
  
  return (
    <div>
      {nonNullChildren.map((child, index) => {
        return React.cloneElement(child, {
          ...props,
          secondary: !child.props.tertiary && !child.props.primary, // In button groups, secondary style is the default.
          grouped: nonNullChildren.length > 1, // Only pass the flag if there are actually more than 1 button in the group.
          groupFirst: index === 0,
          groupLast: index === nonNullChildren.length - 1
        });
      })}
    </div>
  );
}

Button = withRouter(Button); // eslint-disable-line
Button.Group = ButtonGroup;
export default styled(Button)``;